import React from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { MachineInfo } from '../../services/api';
import { useI18n } from '../../i18n/useI18n';

const DEFAULT_ACCENT = '#e86a8a';

export interface DeviceFilterSelectProps {
  value: string;
  onChange: (machineCode: string) => void;
  machines: MachineInfo[];
  accentColor?: string;
  minWidth?: number | string | { xs?: number | string; sm?: number | string };
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  sx?: object;
  /**
   * Dính khi scroll — cùng kiểu thanh Ảnh/Khung/BST (MPhotoSectionTabs).
   * stickyTop mặc định = dưới navbar + hàng tab section.
   */
  sticky?: boolean;
  stickyTop?: number | string | Record<string, number | string>;
}

/** Dropdown lọc gallery theo mã máy (đã lọc sẵn theo product ở parent). */
const DeviceFilterSelect: React.FC<DeviceFilterSelectProps> = ({
  value,
  onChange,
  machines,
  accentColor = DEFAULT_ACCENT,
  minWidth = { xs: 128, sm: 168 },
  fullWidth = false,
  size = 'small',
  sx,
  sticky = false,
  // Navbar 56/64 + hàng tab section (~52px)
  stickyTop = { xs: 108, sm: 116 },
}) => {
  const { pages: t } = useI18n();

  const control = (
    <FormControl
      size={size}
      fullWidth={fullWidth || sticky}
      sx={{
        minWidth: sticky || fullWidth ? 0 : minWidth,
        width: sticky || fullWidth ? '100%' : undefined,
        '& .MuiOutlinedInput-root': {
          borderRadius: 2.5,
          bgcolor: '#fff',
          color: accentColor,
          '& fieldset': { borderColor: alpha(accentColor, 0.28) },
          '&:hover': {
            bgcolor: alpha(accentColor, 0.04),
            '& fieldset': { borderColor: alpha(accentColor, 0.45) },
          },
          '&.Mui-focused': {
            bgcolor: '#fff',
            '& fieldset': { borderColor: accentColor, borderWidth: '1.5px' },
          },
        },
        '& .MuiInputLabel-root': {
          fontWeight: 700,
          fontSize: '0.78rem',
          color: alpha(accentColor, 0.7),
        },
        '& .MuiInputLabel-root.Mui-focused': { color: accentColor },
        '& .MuiSelect-select': {
          fontWeight: 700,
          fontSize: { xs: '0.75rem', sm: '0.82rem' },
          py: 0.85,
          color: accentColor,
        },
        '& .MuiSvgIcon-root': {
          color: alpha(accentColor, 0.75),
        },
        ...sx,
      }}
    >
      <InputLabel id="device-filter-select-label">{t.photos.filterByDevice}</InputLabel>
      <Select
        labelId="device-filter-select-label"
        label={t.photos.filterByDevice}
        value={value}
        onChange={(e) => onChange(String(e.target.value || ''))}
        MenuProps={{
          PaperProps: {
            sx: {
              borderRadius: 2,
              border: '1px solid',
              borderColor: alpha(accentColor, 0.18),
              mt: 0.5,
              '& .MuiMenuItem-root': {
                fontWeight: 600,
                fontSize: '0.82rem',
              },
              '& .MuiMenuItem-root.Mui-selected': {
                bgcolor: alpha(accentColor, 0.12),
                color: accentColor,
              },
              '& .MuiMenuItem-root.Mui-selected:hover': {
                bgcolor: alpha(accentColor, 0.18),
              },
            },
          },
        }}
      >
        <MenuItem value="">
          <em>{t.photos.allDevices}</em>
        </MenuItem>
        {machines.map((m) => (
          <MenuItem key={m.machineCode} value={m.machineCode}>
            {m.machineName?.trim() ? `${m.machineName} (${m.machineCode})` : m.machineCode}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  if (!sticky) return control;

  return (
    <Box
      sx={{
        width: '100%',
        position: { xs: 'sticky', md: 'static' },
        top: stickyTop,
        zIndex: (theme) => theme.zIndex.appBar - 3,
        bgcolor: (theme) => theme.palette.background.default,
        pt: { xs: 0.5, md: 0 },
        pb: { xs: 1, md: 1.25 },
        mb: { xs: 0.75, md: 1.5 },
        // Cùng mép ngang với MPhotoSectionTabs sticky
        mx: { xs: -0.5, md: 0 },
        px: { xs: 0.5, md: 0 },
      }}
    >
      {control}
    </Box>
  );
};

export default DeviceFilterSelect;
